import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, PartPurchaseRepository, ReportService, UtilityService } from 'app/core/core.module';
import { Part, PartLink, PartOption, PartPartType, PartType } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { DxDataGridComponent, DxFileUploaderComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { PartTypesSelectDialogComponent, SharedModule } from '../../shared/shared.module';
import { BulkEditDialogComponent } from '../bulk-edit-dialog/bulk-edit-dialog.component';
import { PartLookupDialogComponent } from '../part-lookup-dialog/part-lookup-dialog.component';
import { PartPurchaseOrderDialogComponent } from '../part-po-dialog/part-po-dialog.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-part-pricing',
    templateUrl: './part-pricing.component.html',
    styleUrls: ['./part-pricing.component.scss'],
    standalone: false,
})
export class PartPricingComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    parts: any;
    orders: any[];
    filter: any;
    isToastVisible = false;
    processing = false;
    showAsDialog = false;
    selectedParts = [];
    selectMode = 'multiple';
    partTypeIds: any;
    partOptions: any;
    mode: any;
    columnLimitMode = false;
    showMfgInfoMode = false;
    partDetailMode = false;
    canSeeCost = false;
    canSeePricing = false;
    orderLinePartsOnNew: any;
    showSideBar = false;
    salesOrderMode = false;
    purchaseOrderMode = false;
    projectionsMode = false;
    vendorId: number;
    showAliases = true;
    storageKey: string = 'part-pricing-grid';
    invalidId: number;
    excludePartIds: number[];
    readOnly = true;

    @ViewChild('partsGrid') partsGrid: DxDataGridComponent;
    @ViewChild('priceUpdateFileUploader') priceUpdateFileUploader: DxFileUploaderComponent;
    onExporting = SharedModule.onExporting;

    router = inject(Router);
    uow = inject(GenesisUnitOfWork);
    modalService = inject(BsModalService);
    constructor(
        public auth: AuthService,
        private appInfo: AppInformationService,
        private utility: UtilityService,
        private partPurchaseRepository: PartPurchaseRepository,
    ) {}

    async ngOnInit() {
        this.setPermissions();
        await this.getData();
    }

    setPermissions() {
        this.canSeeCost = true;
        this.canSeePricing = true;
        this.readOnly = false;
    }

    async updatePricing() {
        await this.partsGrid.instance.saveEditData();

        const data = this.partsGrid.instance.getDataSource().items();
        const ids = data.filter((p) => p._manualRetailPrice && p._manualRetailPrice !== '').map((p) => p.id);

        for (const partId of ids) {
            const partToUpdate = this.parts.find((p) => p.id === partId);
            if (partToUpdate && partToUpdate._manualRetailPrice && !isNaN(partToUpdate._manualRetailPrice)) {
                const originalRetailPrice = partToUpdate.retailPrice;
                partToUpdate.retailPrice = Number(partToUpdate._manualRetailPrice);
                console.log(Number(partToUpdate.getTierPrice(6)));
                partToUpdate.price = Number(partToUpdate.getTierPrice(6));
                partToUpdate.changeLog = `Retail price updated from ${originalRetailPrice} to ${partToUpdate.retailPrice} on ${new Date().toLocaleString()} by ${this.auth.currentUser.userName}`;
            }
        }

        // await this.uow.commit();
        // await this.getData();
    }

    async clearManualEdits() {
        if (!(await confirm('Clear all values in the Edit column?', this.appInfo.settings.company))) return;

        this.partsGrid.instance.cancelEditData();
        this.parts.forEach((part) => {
            part._manualRetailPrice = null;
        });
        this.partsGrid.instance.clearFilter('dataSource');
        await this.partsGrid.instance.refresh();
    }

    async priceUpdateFileSelected(e) {
        const file = e.value && e.value.length > 0 ? e.value[0] : null;
        if (!file) return;

        await this.processPriceUpdateFile(file);
    }

    async processPriceUpdateFile(file: File) {
        const text = await file.text();
        const rows = this.parsePriceUpdateCsv(text);

        if (rows.length === 0) {
            await alert('The selected file is empty.', this.appInfo.settings.company);
            return;
        }

        const headerRow = rows[0].map((h) => h.trim().toLowerCase());
        const idIndex = headerRow.indexOf('id');
        const partNumberIndex = headerRow.indexOf('part number');
        const retailPriceIndex = headerRow.indexOf('retail price');

        if (idIndex === -1 || partNumberIndex === -1 || retailPriceIndex === -1) {
            await alert('The CSV file must include Id, Part Number, and Retail Price columns.', this.appInfo.settings.company);
            return;
        }

        const unmatched: { id: string; partNumber: string }[] = [];
        let matchedCount = 0;

        for (const row of rows.slice(1)) {
            if (row.every((cell) => !cell || !cell.trim())) continue;

            const idText = (row[idIndex] || '').trim();
            const partNumberText = (row[partNumberIndex] || '').trim();
            const priceText = (row[retailPriceIndex] || '').trim();

            const id = Number(idText);
            const price = Number(priceText.replace(/[$,]/g, ''));
            const part = idText && !isNaN(id) ? this.parts.find((p) => p.id === id) : null;

            if (part && !isNaN(price)) {
                part._manualRetailPrice = price;
                matchedCount++;
            } else {
                unmatched.push({ id: idText, partNumber: partNumberText });
            }
        }

        await this.partsGrid.instance.refresh();

        if (unmatched.length > 0) {
            const list = unmatched.map((u) => `Id ${u.id || '(blank)'} - ${u.partNumber || '(blank)'}`).join('\n');
            await alert(`${unmatched.length} id(s) did not match a part and were skipped:\n${list}`, this.appInfo.settings.company);
        }

        if (matchedCount > 0) {
            this.partsGrid.instance.filter(['_manualRetailPrice', '<>', null]);
        }
        this.priceUpdateFileUploader.instance.reset();
    }

    private parsePriceUpdateCsv(text: string): string[][] {
        const rows: string[][] = [];
        let row: string[] = [];
        let field = '';
        let inQuotes = false;

        const pushField = () => {
            row.push(field);
            field = '';
        };
        const pushRow = () => {
            pushField();
            rows.push(row);
            row = [];
        };

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (inQuotes) {
                if (char === '"') {
                    if (text[i + 1] === '"') {
                        field += '"';
                        i++;
                    } else {
                        inQuotes = false;
                    }
                } else {
                    field += char;
                }
            } else if (char === '"') {
                inQuotes = true;
            } else if (char === ',') {
                pushField();
            } else if (char === '\r') {
                pushRow();
                if (text[i + 1] === '\n') i++;
            } else if (char === '\n') {
                pushRow();
            } else {
                field += char;
            }
        }
        if (field.length > 0 || row.length > 0) pushRow();

        return rows.filter((r) => r.length > 1 || (r.length === 1 && r[0] !== ''));
    }

    async getData() {
        console.log('pricingMode | buildDetailBulkAddMode');
        const parts = (await this.uow.getPartsForSearch()) as Part[];
        this.parts = parts;
    }

    async refreshCalcCosts() {
        this.appInfo.pleaseWaitShow();
        await this.utility.getCalculatedCost();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    search() {}
    clear() {}

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.partsGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.partsGrid.instance.clearSelection();
        }
    }

    async save() {
        // await this.partsGrid.instance.saveEditData();
        this.isToastVisible = true;
        this.appInfo.pleaseWaitShow();
        await this.uow.commit();
        // await this.getData();
        this.parts.forEach((part) => {
            part._manualRetailPrice = null;
        });
        this.appInfo.pleaseWaitHide();
    }

    async cancelEdits() {
        this.partsGrid.instance.cancelEditData();
        this.uow.rollback();
        await this.partsGrid.instance.refresh();
    }

    onToolbarPreparing(e) {
        const toolbarItems = e.toolbarOptions.items;
        const saveButton = toolbarItems.filter((item) => item.name === 'saveButton')[0];
        if (saveButton) toolbarItems.splice(toolbarItems.indexOf(saveButton), 1);
        const revertButton = toolbarItems.filter((item) => item.name === 'revertButton')[0];
        if (revertButton) toolbarItems.splice(toolbarItems.indexOf(revertButton), 1);

        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    rowDoubleClick(e) {
        // console.log(e);
        if (!e.data || !e.data.id) return;
        if (e.column.name === 'id' || e.column.name === 'inStock' || e.column.name === 'allocated' || e.column.name === 'projectedInventory') this.open(e.data.id);
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (!!e.data.cost && !!e.data.calculatedCost && e.data.calculatedCost !== 0) {
                if (e.data.calculatedCost > e.data.cost) e.rowElement.className = e.rowElement.className + ' part-search-yellow-grid';
                if (e.data.calculatedCost > e.data.cost * 2) e.rowElement.className = e.rowElement.className + ' part-search-red-grid';
            }
            if (!!e.data.fontColor) {
                e.rowElement.style.color = `${e.data.fontColor}`;
                e.rowElement.style.fontWeight = 'bold';
            }
        }
    }

    setFontColor() {
        const selectedRows = this.partsGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'color',
                dialogHeader: 'Set Font Color',
                dialogPrompt: 'Select Font Color',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
                selectedRows.forEach((row) => {
                    row.fontColor = result.value;
                });
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async open(partId: number) {
        this.router.navigate(['/parts/' + (partId ? partId : 'new')]);
    }

    cancel() {
        if (this.selectedParts.length !== 0) {
            this.appInfo.notifyToast('Removing added parts', 'info', 1000);
        }
        this.result.next({ success: false });
    }

    closeDialog() {
        if (this.showAsDialog) {
            const selectedPartIds = this.selectedParts.map((x) => {
                return x.id;
            });
            this.result.next({ success: this.selectedParts.length > 0 ? true : false, partIds: selectedPartIds.length > 0 ? selectedPartIds : null });
        }
    }
}
class BulkInfo {
    vendor: any;
    category: any;
    fontColor: any;
    isAssembly: any;
}
